import type { Catalog } from "../en";

/**
 * Web app portal - equipment and after-service - Filipino (Taglish where natural; follows the widget's equipment words in
 * `apps/widget/src/equipment/copy-fil.ts`). A warranty badge states a status and a date - never a coverage the workspace did not link
 * terms for.
 */
export const webappPortalAfterSales: Catalog["webapp"]["portal"]["afterSales"] = {
  nav: { area: "Kagamitan at serbisyo", equipment: "Kagamitan", service: "Serbisyo", contracts: "Mga kontrata", projects: "Mga installation", visits: "Mga pagbisita", returns: "Mga return" },
  off: { title: "Hindi available dito ang kagamitan at serbisyo", body: "Hindi ipinapakita ng workspace na ito ang kagamitan, pagbisita o installation sa portal. Puwede ka pa ring mag-report ng request at tutulungan ka namin.", cta: "Pumunta sa mga ticket ko" },
  common: {
    retry: "Subukan ulit",
    loadMore: "Magpakita pa",
    none: "Walang tala",
    offline: "Mukhang offline ka. Tingnan ang koneksyon mo at subukan ulit.",
    limited: "Ilang beses mo na itong sinubukan. Maghintay sandali, saka subukan ulit.",
    notFound: "Hindi namin ito makita. Baka sa ibang account ito.",
    forbidden: "Wala kang access dito.",
    failed: "May problema sa panig namin. Pakisubukan ulit.",
    downloaded: "Na-download ang {{name}}",
    downloadFailed: "Hindi namin ma-download ang file. Pakisubukan ulit.",
  },
  warranty: {
    until: "Sakop ng warranty hanggang {{date}}",
    endsIn_one: "Matatapos ang warranty sa {{count}} araw",
    endsIn_other: "Matatapos ang warranty sa {{count}} araw",
    expired: "Nag-expire ang warranty noong {{date}}",
    unknown: "Walang tala ng warranty",
    ends: "Matatapos ang warranty sa {{date}}",
    covers: "Sakop ng naka-link na warranty terms ang: {{cover}}.",
    teamConfirms: "Kukumpirmahin ng team kung ano ang sakop ng warranty.",
    cover: { parts: "piyesa", labor: "labor", travel: "biyahe", onsite: "serbisyo sa site" },
    filter: { in_warranty: "Sakop ng warranty", expiring: "Malapit nang mag-expire", expired: "Expired na", unknown: "Walang tala" },
  },
  equipment: {
    title: "Aking kagamitan",
    count_one: "{{count}} unit",
    count_other: "{{count}} unit",
    search: "Maghanap ng kagamitan",
    searchHint: "Pangalan, tag o serial",
    filter: "Warranty",
    allWarranty: "Lahat ng kagamitan",
    categoryFilter: "Kategorya",
    allCategories: "Lahat ng kategorya",
    clear: "I-clear ang paghahanap",
    emptyTitle: "Wala pang kagamitan",
    emptyBody: "Lalabas dito ang kagamitan ng organisasyon mo kapag idinagdag na ito ng team. Puwede ka pa ring mag-report ng problema at tutulungan ka namin.",
    emptyFiltered: "Walang tugmang kagamitan",
    emptyFilteredHint: "Subukan ang ibang pangalan, tag o serial number, o i-clear ang filter.",
    errorTitle: "Hindi namin ma-load ang kagamitan mo",
    open_: "Buksan ang {{name}}, {{tag}}",
    back: "Bumalik sa aking kagamitan",
    detailErrorTitle: "Hindi namin ma-load ang kagamitang ito",
    notFoundTitle: "Hindi namin makita ang kagamitang ito",
    notFoundBody: "Baka nailipat ito, o sa ibang account ito. Bumalik sa listahan mo para makita ang kagamitan mo.",
    labelTitle: "Hindi para sa account mo ang label na ito",
    detailsTitle: "Detalye",
    serial: "Serial number",
    model: "Model",
    category: "Kategorya",
    location: "Lokasyon",
    installed: "Na-install",
    warrantyTitle: "Warranty",
    contractTitle: "Service contract",
    noContract: "Walang service contract na sumasakop sa kagamitang ito.",
    documentsTitle: "Mga dokumento",
    noDocuments: "Wala pang naibabahaging dokumento para sa kagamitang ito.",
    download: "Buksan",
    openDoc: "Buksan ang {{title}}",
    visitsTitle: "Mga pagbisita",
    noVisits: "Wala pang pagbisita para sa kagamitang ito.",
    report: "Mag-report ng problema sa kagamitang ito",
    requestReturn: "Humiling ng return",
  },
  contracts: {
    title: "Aking mga kontrata",
    intro: "Ano ang kasama sa mga service contract mo at gaano na ang nagamit mo. Hindi ipinapakita rito ang presyo at internal na terms.",
    emptyTitle: "Wala pang service contract",
    emptyBody: "Kapag may service contract na para sa organisasyon mo, lalabas dito ang mga petsa at kung ano ang kasama.",
    errorTitle: "Hindi namin ma-load ang mga kontrata mo",
    valid: "Valid mula {{from}} hanggang {{to}}",
    daysLeft_one: "{{count}} araw na lang",
    daysLeft_other: "{{count}} araw na lang",
    covers: "Sakop",
    cover: { labor: "labor", parts: "piyesa", travel: "biyahe" },
    coverNone: "tanungin ang team kung ano ang kasama",
    exclusions: "Hindi kasama: {{text}}",
    allAssets: "Sakop ang lahat ng kagamitan mo. Tingnan ang aking kagamitan",
    someAssets_one: "Sakop ang {{count}} unit",
    someAssets_other: "Sakop ang {{count}} unit",
    entitlements: "Kasama sa kontrata",
    remaining: "{{left}} sa {{total}} ang natitira",
    kind: { pm_visits: "Preventive maintenance na pagbisita", calibrations: "Calibration", onsite_visits: "Pagbisita sa site", remote_support_hours: "Oras ng remote support", ticket_allowance: "Mga request" },
    period: { contract: "Para sa buong kontrata", year: "Magre-reset sa {{date}}", month: "Magre-reset sa {{date}}" },
  },
  projects: {
    // Ang mga neutral na salita, gamit hanggang alam na ang bokabularyo at kapag walang plural dito.
    words: { project: "Installation", projects: "Mga installation project", stage: "Stage", stages: "Mga stage", area: "Mga installation" },
    title: "Aking {{areaLc}}",
    intro: "Ang {{projectsLc}} mo: nasaan na ang bawat isa, ang mga bukas na item na puwede mong kumpirmahin, at ang acceptance.",
    emptyTitle: "Wala pang {{projectLc}}",
    emptyBody: "Lalabas dito ang {{projectsLc}} ng organisasyon mo habang isinasagawa ang mga ito.",
    errorTitle: "Hindi namin ma-load ang {{areaLc}} mo",
    detailErrorTitle: "Hindi namin ma-load ang {{projectLc}} na ito",
    notFoundTitle: "Hindi namin makita ang {{projectLc}} na ito",
    back: "Bumalik sa aking {{areaLc}}",
    open_: "Buksan ang {{projectLc}} {{number}}, {{name}}",
    stages: "{{done}} sa {{total}} na {{stageLc}} ang tapos",
    openItems_one: "{{count}} bukas na item",
    openItems_other: "{{count}} bukas na item",
    acceptedOn: "Tinanggap noong {{date}}",
    plannedEnd: "Planadong matapos sa {{date}}",
    assets: "Kagamitan",
    progress: "Progreso",
    documents: "Mga dokumento",
    noDocuments: "Wala pang naibabahaging dokumento para sa {{projectLc}} na ito.",
    checklist: "{{done}}/{{total}} na check",
    status: { planned: "Planado", in_progress: "Isinasagawa", punch_list: "Mga bukas na item", ready_for_acceptance: "Handa na para sa acceptance", handed_over: "Naipasa na", on_hold: "Naka-hold", cancelled: "Kinansela" },
    stageStatus: { pending: "Hindi pa nasisimulan", in_progress: "Isinasagawa", passed: "Tapos na", failed: "Kailangang ulitin" },
  },
  punch: {
    status: { open: "Bukas", in_progress: "Inaayos", fixed: "Naayos - pakikumpirma", verified: "Nakumpirma", reopened: "Muling binuksan", waived: "Sarado" },
    empty: "Walang bukas. Mag-raise ng issue kung may napansin kang hindi tama.",
    raise: "Mag-raise ng issue",
    raiseTitle: "Mag-raise ng issue",
    raiseHint: "Itatala namin ito sa {{projectLc}} at magbubukas kami ng request para makausap mo ang team tungkol dito.",
    raiseSubmit: "Ipadala",
    cancel: "Kanselahin",
    fieldTitle: "Ano ang mali?",
    fieldDescription: "Karagdagang detalye (opsyonal)",
    fieldLocation: "Saan (opsyonal)",
    fieldAsset: "Kagamitan (opsyonal)",
    noAsset: "Hindi tungkol sa isang unit",
    titleRequired: "Sabihin sa ilang salita kung ano ang mali.",
    raisedToast: "Na-raise ang issue. Sundan ito sa request {{number}}.",
    raisedPlain: "Na-raise ang issue",
    confirmFixed: "Kumpirmahing naayos",
    notFixed: "Hindi pa naayos",
    verifyGroup: "Naayos na ba ang {{number}}?",
    confirmedToast: "Nakumpirmang naayos ang {{number}}",
    reopenedToast: "Ibinalik sa team ang {{number}}",
    fixNote: "Sabi ng team: {{note}}",
    ticket: "Request {{number}}",
  },
  accept: {
    title: "Acceptance",
    ready: "Na-verify na ang lahat. Puwede nang tanggapin ang {{projectLc}}.",
    blocked: "Hindi pa matatanggap ang {{projectLc}} na ito:",
    blockedStage: "Hindi pa pumapasa ang {{stageLc}} na \"{{stage}}\".",
    blockedItems_one: "{{count}} bukas na item pa ang kailangang kumpirmahin.",
    blockedItems_other: "{{count}} bukas na item pa ang kailangang kumpirmahin.",
    adminOnly: "Admin lang ng organisasyon ang puwedeng pumirma ng acceptance. Hilingin sa isang administrator ng organisasyon mo na tanggapin ang {{projectLc}} na ito.",
    accepted: "Tinanggap noong {{date}} ni {{name}}. Tumatakbo na ang warranty ng kagamitan.",
    comment: "Komento: {{text}}",
    start: "Suriin at tanggapin",
    certificate: "I-download ang certificate",
    preview: "Tingnan ang preview ng certificate",
    step: "Hakbang {{n}} sa {{total}}",
    reviewTitle: "Suriin ang {{projectLc}}",
    reviewBody: "{{assets}} unit, {{stages}} {{stageLc}} ang tapos at {{items}} item sa listahan, lahat ay na-verify o sarado na. Tingnan ang kagamitan, {{stagesLc}}, mga item at mga dokumento sa itaas bago magpatuloy.",
    reviewed: "Nasuri ko na ang kagamitan, {{stagesLc}}, mga item at mga dokumento sa itaas.",
    continue: "Magpatuloy",
    back: "Bumalik",
    codeTitle: "Ilagay ang code na ipinadala namin",
    codeSent: "Kailangan ng isang beses na code para pumirma. Nagpadala kami ng 6-digit na code sa {{email}}.",
    codeLabel: "6-digit na code",
    codeBad: "Ilagay ang 6 na digit ng code.",
    resend: "Magpadala ng bagong code",
    signTitle: "Pumirma",
    method: "Paano ka pipirma",
    draw: "Iguhit",
    type: "I-type ang pangalan",
    padLabel: "Signature pad. Gamitin ang \"I-type ang pangalan\" kung hindi ka makaguhit.",
    clear: "I-clear",
    typedLabel: "I-type ang buong pangalan mo",
    typedHint: "Kasing-bisa ng iginuhit na pirma ang na-type na pangalan.",
    titleLabel: "Titulo mo (opsyonal)",
    commentsLabel: "Komento (opsyonal)",
    confirm: "Tinatanggap ko ang {{projectLc}} na {{name}} sa ngalan ng organisasyon ko. Magsisimula ang warranty ng bawat unit sa petsa ng pagpirma ko.",
    submit: "Pumirma at tanggapin",
    problem: {
      wrong_code: "Hindi tama ang code. Tingnan at subukan ulit.",
      code_expired: "Nag-expire na ang code. Nagpadala kami ng bago.",
      locked: "Masyadong maraming pagsubok. Nagpadala kami ng bagong code.",
      admin_only: "Admin lang ng organisasyon ang puwedeng tumanggap ng {{projectLc}}.",
      not_ready: "Hindi na handa para sa acceptance ang {{projectLc}} na ito. I-reload ang page para makita kung bakit.",
      signature: "Hindi namin mabasa ang pirma. Pakipirmahan ulit.",
      limited: "Ilang beses mo na itong sinubukan. Maghintay sandali, saka subukan ulit.",
      offline: "Mukhang offline ka. Tingnan ang koneksyon mo at subukan ulit.",
      failed: "Hindi namin naitala ang acceptance. Pakisubukan ulit.",
    },
  },
  visits: {
    title: "Aking mga pagbisita",
    intro: "Ang mga pagbisita ng mga engineer namin sa site mo, nakaraan at paparating.",
    upcoming: "Paparating ({{count}})",
    past: "Nakaraan ({{count}})",
    emptyUpcoming: "Walang paparating na pagbisita",
    emptyPast: "Wala pang nakaraang pagbisita",
    emptyBody: "Kapag nag-schedule ang team ng pagbisita para sa isa sa mga request mo, lalabas ito rito kasama ang oras at ang engineer.",
    errorTitle: "Hindi namin ma-load ang mga pagbisita mo",
    detailErrorTitle: "Hindi namin ma-load ang pagbisitang ito",
    notFoundTitle: "Hindi namin makita ang pagbisitang ito",
    back: "Bumalik sa aking mga pagbisita",
    open_: "Buksan ang pagbisita {{number}}",
    chooseBadge: "Pumili ng oras",
    waitingChoice: "Naghihintay ng napili mong oras",
    status: { proposed: "Pumili ng oras", scheduled: "Naka-schedule", dispatched: "Papunta na ang engineer", on_site: "Nasa site ang engineer", completed: "Natapos", cannot_complete: "Hindi natapos", cancelled: "Kinansela", no_access: "Walang access" },
    kind: { corrective: "Pagbisita para sa ayos", pm: "Preventive maintenance", calibration: "Calibration", installation: "Installation", inspection: "Inspeksyon", training: "Training" },
    chooseTitle: "Pumili ng oras ng pagbisita",
    chooseBody: "Piliin ang oras na pasok sa iyo. Kukumpirmahin namin agad.",
    windows: "Mga oras ng pagbisita",
    confirmWindow: "Kumpirmahin ang oras na ito",
    taken: "Kakakuha lang ng oras na iyon. Pumili ng ibang oras sa ibaba.",
    takenNone: "Kakakuha lang ng oras na iyon at wala nang ibang oras na iniaalok ngayon. Makikipag-ugnayan sa iyo ang team para magkasundo sa oras.",
    chosenToast: "Nakumpirma ang pagbisita",
    scheduleTitle: "Pagbisita",
    engineer: "Engineer",
    where: "Saan",
    kindLabel: "Uri",
    equipment: "Kagamitan",
    prep: "Bago kami dumating",
    calendar: "Idagdag sa calendar (.ics)",
    reschedule: "Humiling ng ibang oras",
    rescheduleTitle: "Humiling ng ibang oras",
    rescheduleLabel: "Kailan ka puwede?",
    rescheduleHint: "Sabihin ang mga araw o oras na puwede. Kukumpirmahin ng team ang bagong oras.",
    rescheduleSubmit: "Ipadala ang hiling",
    rescheduleSent: "Naipadala ang hiling. Kukumpirmahin ng team ang bagong oras.",
    rescheduleRequested: "Humiling ka ng ibang oras. Kukumpirmahin ito ng team.",
    noteShort: "Dagdagan pa ng kaunti (hindi bababa sa {{min}} na character).",
    cancel: "Kanselahin ang pagbisita",
    cancelTitle: "Kanselahin ang pagbisitang ito",
    cancelLabel: "Dahilan (opsyonal)",
    cancelHint: "Puwede kang magkansela hanggang 24 oras bago ang pagbisita.",
    cancelSubmit: "Kanselahin ang pagbisita",
    cancelled: "Kinansela ang pagbisita",
    keep: "Panatilihin ang pagbisita",
    reportTitle: "Ulat ng pagbisita",
    resolved: "Naayos ang problema sa pagbisitang ito.",
    notResolved: "Hindi pa lubusang naayos ang problema sa pagbisitang ito.",
    followUp: "Kailangan ng follow-up na pagbisita. Makikipag-ugnayan ang team.",
    work: "Ginawa",
    findings: "Natuklasan",
    parts: "Mga ginamit na piyesa",
    time: "Oras sa site",
    minutes_one: "{{count}} minuto",
    minutes_other: "{{count}} minuto",
    signedBy: "Pinirmahan ni",
    reportPdf: "Ulat ng pagbisita (PDF)",
  },
  returns: {
    title: "Aking mga return",
    intro: "Ang mga item na ibinalik mo o ibabalik mo pa, at kung nasaan na ang bawat isa.",
    emptyTitle: "Wala pang return",
    emptyBody: "Kapag nagbalik ka ng item para ipaayos o ipapalit, masusundan mo ito rito.",
    toEquipment: "Pumunta sa aking kagamitan",
    errorTitle: "Hindi namin ma-load ang mga return mo",
    detailErrorTitle: "Hindi namin ma-load ang return na ito",
    notFoundTitle: "Hindi namin makita ang return na ito",
    back: "Bumalik sa aking mga return",
    open_: "Buksan ang return {{number}}",
    withManufacturer: "Nasa manufacturer ang item mo. I-a-update namin ang page na ito kapag nakabalik na ito.",
    detailsTitle: "Return",
    item: "Item",
    request: "Request",
    opened: "Binuksan",
    instructions: "Paano ito ipapadala",
    tracking: "Tracking",
    noTracking: "Wala pang tracking reference.",
    trackingLabel: "Tracking reference ng parcel mo",
    trackingHint: "Ang numerong ibinigay ng courier mo, para masundan namin ang parcel.",
    trackingSave: "I-save ang reference",
    trackingRequired: "Ilagay ang tracking reference.",
    trackingSaved: "Na-save ang tracking reference",
  },
  field: {
    label: "Alin sa kagamitan mo ang tinutukoy nito?",
    notListed: "Wala sa listahan",
    more: "Ipinapakita ang {{shown}} sa {{total}}. Maghanap para makita ang iba.",
    failed: "Hindi namin ma-load ang kagamitan mo. Puwede mo na lang itong ilarawan sa detalye.",
    about: "Tungkol ang request na ito sa {{name}} ({{tag}}).",
  },
};
